import React, { useEffect } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import { Input } from "../../../../shadcn/components/ui/input";
import CustomModalLayoutV2 from "../../../bidder/Tabs/auctionFloor/components/CustomModalLayoutV2";

export type UserProfileMissingField = "mobile";

interface UserProfileMissingFieldsConfig {
  fields: UserProfileMissingField[];
  title: string;
  description: string;
}

interface PDAskUserMissingDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Record<UserProfileMissingField, string>) => void;
  config: UserProfileMissingFieldsConfig;
  values: Record<UserProfileMissingField, string>;
  setValue: (field: UserProfileMissingField, value: string) => void;
  loading?: boolean;
}

const mobileRegEx = /^[0-9]{10}$/;

const validators: Record<
  UserProfileMissingField,
  (v: string) => string | null
> = {
  mobile: (v) =>
    !mobileRegEx.test(v) ? "Enter a valid 10-digit mobile number" : null,
};

const inputClassName =
  "h-10 text-sm px-3 rounded-md border border-gray-300 focus-visible:ring-1 focus-visible:ring-brand-primaryAction";

export function PDAskUserMissingDetailsModal({
  open,
  onClose,
  onSubmit,
  config,
  values,
  setValue,
  loading = false,
}: PDAskUserMissingDetailsModalProps) {
  const [errors, setErrors] = React.useState<
    Partial<Record<UserProfileMissingField, string>>
  >({});

  useEffect(() => {
    if (open) {
      setErrors({});
    }
  }, [open]);

  const handleChange = (field: UserProfileMissingField, value: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }

    setValue(field, value);
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    config.fields.forEach((field) => {
      const error = validators[field](values[field] || "");
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    onSubmit(values);
  };

  const renderField = (field: UserProfileMissingField) => {
    switch (field) {
      case "mobile":
        return (
          <Input
            type="tel"
            placeholder="Enter 10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
            value={values.mobile || ""}
            onChange={(e) =>
              handleChange("mobile", e.target.value.replace(/\D/g, ""))
            }
            className={inputClassName}
          />
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({});
  };

  return (
    <CustomModalLayoutV2
      open={open}
      blurBackground
      onClose={handleClose}
      childrenClassName="pb-6 px-6"
      className="pt-8 mx-4 w-full md:w-[400px]"
    >
      <div className="flex flex-col gap-6">
        <div className="text-center">
          <div className="font-semibold text-lg">{config.title}</div>
          <div className="text-xs text-brand-textGrey mt-1">
            {config?.description}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {config?.fields?.map((field, index) => (
            <FieldWrapper
              key={`pd_ask_user_missing_details_field_wrapper_${index}`}
              error={errors[field]}
            >
              {renderField(field)}
            </FieldWrapper>
          ))}
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-brand-primaryAction"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
    </CustomModalLayoutV2>
  );
}

const FieldWrapper = ({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: string;
}) => {
  return (
    <div className="flex flex-col gap-1">
      {children}
      {error && <p className="text-xs text-red-500 leading-none">{error}</p>}
    </div>
  );
};
